import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { unwrapApiData } from '../../../../app/infrastructure/http/unwrapApiData';
import type { RootState } from '../../../../store/store';
import type {
  ForgotPasswordRequest,
  GoogleSignInRequest,
  SignInRequest,
  SignUpRequest,
  WhoAmIResponse,
} from '../../application/dto/Auth.dto';

const API_HOST = process.env.REACT_APP_API_HOST || '/v1/';
const API_BASE_URL = API_HOST.endsWith('/') ? API_HOST : `${API_HOST}/`;

/**
 * Auth HTTP — cookie session is canonical (`credentials: 'include'`).
 * Optional Bearer kept only if `auth.accessToken` is already in memory (hybrid).
 *
 * Identity comes from GET /auth/whoami, not from sign-in response mapping.
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth?.accessToken;
      if (token) {
        headers.set(
          'Authorization',
          token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        );
      }
      return headers;
    },
  }),
  endpoints: build => ({
    whoAmI: build.query<WhoAmIResponse, void>({
      query: () => '/auth/whoami',
      transformResponse: (response: unknown) =>
        unwrapApiData<WhoAmIResponse>(response),
    }),

    signIn: build.mutation<void, SignInRequest>({
      query: body => ({
        url: '/auth/sign-in',
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
    }),

    signInWithGoogle: build.mutation<void, GoogleSignInRequest>({
      query: body => ({
        url: '/auth/google',
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
    }),

    signUp: build.mutation<void, SignUpRequest>({
      query: body => ({
        url: '/auth/sign-up',
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
    }),

    signUpWithGoogle: build.mutation<void, GoogleSignInRequest>({
      query: body => ({
        url: '/auth/google/sign-up',
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
    }),

    forgotPassword: build.mutation<void, ForgotPasswordRequest>({
      query: body => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
    }),
  }),
});

export const {
  useWhoAmIQuery,
  useLazyWhoAmIQuery,
  useSignInMutation,
  useSignInWithGoogleMutation,
  useSignUpWithGoogleMutation,
  useSignUpMutation,
  useForgotPasswordMutation,
} = authApi;
